import { computed } from 'vue'
import { getAllToyStats } from '@/utils/storage'

/**
 * 排序功能组合函数
 * @param {Ref|ComputedRef} itemsRef - 要排序的项目数组的ref
 * @param {Ref|string} sortByRef - 排序方式的ref或值
 * @param {Ref|string} sortOrderRef - 排序顺序的ref或值 'asc' | 'desc'
 * @returns {Object} 包含sortedItems的对象
 */
export function useSorting(itemsRef, sortByRef = 'default', sortOrderRef = 'desc') {
  const sortedItems = computed(() => {
    // 处理itemsRef（可能是ref或computed）
    const items = itemsRef.value || itemsRef
    if (!items || items.length === 0) {
      return []
    }

    // 处理sortBy和sortOrder（可能是ref或值）
    const sortBy = typeof sortByRef === 'object' && 'value' in sortByRef ? sortByRef.value : sortByRef
    const sortOrder = typeof sortOrderRef === 'object' && 'value' in sortOrderRef ? sortOrderRef.value : sortOrderRef

    const itemsCopy = [...items]
    const stats = getAllToyStats()

    switch (sortBy || 'default') {
      case 'createdAt':
        // 按创建时间排序
        return itemsCopy.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
        })

      case 'likes':
        // 按点赞数排序
        return itemsCopy.sort((a, b) => {
          const likesA = stats[a.id]?.likes || a.likes || 0
          const likesB = stats[b.id]?.likes || b.likes || 0
          return sortOrder === 'desc' ? likesB - likesA : likesA - likesB
        })

      case 'favorites':
        // 按收藏数排序
        return itemsCopy.sort((a, b) => {
          const favoritesA = stats[a.id]?.favorites || a.favorites || a.saves || 0
          const favoritesB = stats[b.id]?.favorites || b.favorites || b.saves || 0
          return sortOrder === 'desc' ? favoritesB - favoritesA : favoritesA - favoritesB
        })

      case 'views':
        // 按观看数排序
        return itemsCopy.sort((a, b) => {
          const viewsA = stats[a.id]?.views || a.views || 0
          const viewsB = stats[b.id]?.views || b.views || 0
          return sortOrder === 'desc' ? viewsB - viewsA : viewsA - viewsB
        })

      case 'title':
        // 按标题排序
        return itemsCopy.sort((a, b) => {
          const titleA = a.title || ''
          const titleB = b.title || ''
          const result = titleA.localeCompare(titleB, 'zh-CN')
          return sortOrder === 'desc' ? -result : result
        })

      case 'default':
      default:
        // 默认顺序（保持原顺序）
        return itemsCopy
    }
  })

  return {
    sortedItems
  }
}

/**
 * 排序选项配置
 */
export const SORT_OPTIONS = [
  { value: 'default', label: '默认顺序', icon: '📋' },
  { value: 'createdAt', label: '创建时间', icon: '📅' },
  { value: 'likes', label: '点赞数', icon: '👍' },
  { value: 'favorites', label: '收藏数', icon: '⭐' },
  { value: 'views', label: '观看数', icon: '👁️' },
  { value: 'title', label: '标题', icon: '🔤' }
]

